/**
 * Collection Result Card Component
 * Displays a collection in search results
 */

import { Card, CardContent, CardActionArea, Box, Typography, Chip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import {
  EmojiEvents as BadgeIcon,
  Explore as AdventureIcon,
} from '@mui/icons-material'
import type { CollectionSearchResult } from '../../../api/types/search'

interface CollectionResultCardProps {
  collection: CollectionSearchResult
}

export function CollectionResultCard({ collection }: CollectionResultCardProps) {
  const navigate = useNavigate()

  const handleClick = () => {
    navigate(`/collections/${collection.id}`)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <Box sx={{ position: 'relative', mb: 2, mt: 3 }}>
      <Chip
        label="Collection"
        size="small"
        color="warning"
        sx={{
          position: 'absolute',
          top: -10,
          left: 16,
          zIndex: 1,
        }}
      />
      <Card
        sx={{
          mt: 0,
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
        }}
      >
        <CardActionArea onClick={handleClick}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="h6" component="h3" gutterBottom>
              {collection.name}
            </Typography>

            {collection.shortDescription && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {collection.shortDescription}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AdventureIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {collection.totalAdventureCount} adventure{collection.totalAdventureCount !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {collection.badgeCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BadgeIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {collection.badgeCount} badge{collection.badgeCount !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Chip
                label={collection.isPublic ? 'Public' : 'Private'}
                size="small"
                variant="outlined"
                color={collection.isPublic ? 'success' : 'default'}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Created by {collection.creatorName} on {formatDate(collection.createdDate)}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  )
}
